import { CSSProperties, useEffect } from "react"
import { app } from "../app"
import { BlockChannels, BlockImage, BlockInfo, BlockRichText, BlockServices, BlockText, BlockMission, Footer } from "."
import { BlockCategories } from "./blockcategories"
import { BlockSections } from "./blocksections"
import { BlockAbout } from "./blockabout"
import { BlockContact } from "./blockcontact"
import { BlockPopup } from "./blockpopup"
import { CookieBanner } from "./cookie-banner"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { languages } from "../locale"
import { generateHreflangTag, getLocaleByZendesk } from "../helpers"
import { Helmet } from "react-helmet"

export function Blocks() {
  let { locale } = useParams()
  const location = useLocation()
  const languageSelected = locale ? getLocaleByZendesk(locale.toLowerCase()) : (localStorage.getItem('preferredLanguage') ? localStorage.getItem('preferredLanguage') : app.locale)
  app.locale = languageSelected
  const urlLocale = languages[languageSelected]?.zendesk as string ?? languageSelected
  const blocks = app.page.content || []
  const navigate = useNavigate()
  const popupBlock = blocks.find((b) => b.type === "popup") as BlockPopup | undefined

  const blockview = blocks.map((b, i) => {
    if (b.type === "popup") return null
    if (b.type === "text") return <BlockText key={b.sort} block={b as BlockText} />
    if (b.type === "richtext") return <BlockRichText key={b.sort} block={b as BlockRichText} />
    if (b.type === "services") return <BlockServices key={b.sort} block={b as BlockServices} />
    if (b.type === "channels") return <BlockChannels key={b.sort} block={b as BlockChannels} />
    if (b.type === "image") return <BlockImage key={b.sort} block={b as BlockImage} />
    if (b.type === "info") return <BlockInfo key={b.sort} block={b as BlockInfo} />
    if (b.type === "categories") return <BlockCategories key={b.sort} block={b as BlockCategories} />
    if (b.type === "sections") return <BlockSections key={b.sort} block={b as BlockSections} />
    if (b.type === "mission") return <BlockMission key={b.sort} block={b as BlockMission} />
    if (b.type === "about") return <BlockAbout key={b.sort} block={b as BlockAbout} />
    if(b.type == "Contact") return <BlockContact key={b.sort} block={b as BlockContact} />
    return null
  })

  useEffect(() => {
    const targetPath = `/${urlLocale.toLowerCase()}`
    if (location.pathname.toLowerCase() !== targetPath) {
      navigate(`${targetPath}${location.search}${location.hash}`, { replace: true })
    }
  }, [urlLocale, location.pathname, location.search, location.hash, navigate])

  /*
   * Native anchor scrolling only fires on load, before CMS content exists.
   * Retried on an interval because a block's content can mount later still.
   */
  useEffect(() => {
    if (!location.hash) return

    const id = decodeURIComponent(location.hash.slice(1))
    let attempts = 0

    const tryScroll = () => {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" })
        return true
      }
      // ~3s of retries, then give up rather than spin forever on a bad anchor.
      return ++attempts > 30
    }

    if (tryScroll()) return
    const timer = setInterval(() => { if (tryScroll()) clearInterval(timer) }, 100)
    return () => clearInterval(timer)
  }, [location.hash, blocks.length])

  const hreflangTags = generateHreflangTag()

  return <div className="overflow-y-auto overflow-x-hidden h-full flex justify-center w-screen">
    <Helmet>
      {hreflangTags?.map((tag, index) => (
        <link key={index} rel="alternate" hrefLang={tag.hreflang} href={tag.href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_PUBLIC_SITE_URL}`} />
    </Helmet>
    <div className="w-full">
      {blockview}
      <Footer />
      <CookieBanner />
      {popupBlock && <BlockPopup block={popupBlock} />}
    </div>
  </div>

}

Blocks.buildStyle = (block: Block) => {

  const styles: CSSProperties = {}

  if (block) {
    if (block.textsize) styles.fontSize = `${block.textsize}%`
    if (block.textcolor) styles.color = block.textcolor
    if (block.bgcolor) styles.backgroundColor = block.bgcolor
    if (block.fontweight) styles.fontWeight = block.fontweight
  }

  return styles

}